import React, { FC, useCallback, useEffect, useMemo } from 'react'
import { Controller } from 'react-hook-form'
import { IControlledSelectProps } from 'transfix-form/components/ControlledSelect'
import { ISelectProps, Select } from 'transfix-ui/components/Select'
import { useCarrierMembershipApi } from 'apps/fleet-planner/hooks'
import { IMotorCarrierUserForCurrentUserFragment } from 'apps/fleet-planner/hooks/useCarrierMembershipApi/graphql/MotorCarrierUserForCurrentUserFragment'

export interface IControlledMotorCarrierUserSelectProps
  extends Pick<IControlledSelectProps, 'control' | 'name' | 'SelectProps'> {
  /**
   * @example { id: '123', type: 'dispatcher', motorCarrier: { id: '123', name: 'Trucker Inc.', dotNumber: 1234567, mcNumber: 7654321 } }
   */
  defaultValue?: IMotorCarrierUserForCurrentUserFragment
  SelectProps: Omit<ISelectProps, 'options' | 'value' | 'onBlur' | 'onChange'>
}
/**
 * @description Essentially functions as a carrier select component, except that the options are all of the
 * `MotorCarrierUser` records associated with the current user. This means that the selected value contains information
 *  about the user's relationship to the carrier, in addition to the carrier's information itself.
 */
export const ControlledMotorCarrierUserSelect: FC<IControlledMotorCarrierUserSelectProps> = ({
  control,
  defaultValue,
  name,
  SelectProps = {},
}) => {
  const { fetchMotorCarrierUsers, motorCarrierUsers } = useCarrierMembershipApi()

  /**
   * @description Fetch list of `MotorCarrierUser` records on mount.
   */
  useEffect(() => {
    fetchMotorCarrierUsers()
  }, [fetchMotorCarrierUsers])

  const findMotorCarrierUserById = useCallback(
    (id: string) => motorCarrierUsers.find(mcu => mcu.id === id),
    [motorCarrierUsers]
  )

  const options = useMemo(
    () =>
      motorCarrierUsers.map(
        ({ id: motorCarrierUserId, motorCarrier: { name: motorCarrierName } }) => ({
          /**
           * Name will always be defined since `motor_carriers.legal_name` is a non-nullable column. However, the GraphQL
           * field is erroneously defined as nullable, hence the default to an empty string.
           */
          label: motorCarrierName ?? '',
          value: motorCarrierUserId,
        })
      ),
    [motorCarrierUsers]
  )

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ onBlur, onChange, value }) => {
        const changeHandler: ISelectProps['onChange'] = ({
          target: { value: motorCarrierUserId },
        }) => {
          let motorCarrierUser: IMotorCarrierUserForCurrentUserFragment | undefined

          if (motorCarrierUserId) {
            motorCarrierUser = findMotorCarrierUserById(String(motorCarrierUserId))
          }

          onChange(motorCarrierUser)
        }

        return (
          <Select
            onBlur={onBlur}
            onChange={changeHandler}
            options={options}
            value={value?.id ?? ''}
            {...SelectProps}
          />
        )
      }}
    />
  )
}

export default ControlledMotorCarrierUserSelect
